import FVector from '@data/geometry/FVector';
import { FacadeComponentType, FloorBasedFacadeState, VerticalFacadeCoverageMode } from '@visualizer/types';
import { SensorData } from 'Simulations/SensorPoint';
import * as THREE from 'three';
import { tagDesignSchemeSurfaceMaterial } from '../../../DesignSchemeSurfaceMaterialHelper';
import { BalconyComponent, BalconyConfig } from './Balcony';
import { Component, FacadeComponentContext } from './Component';
import { GreenComponent, GreenConfig } from './Green';
import { PvPanelsComponent, PvPanelsConfig } from './PvPanels';
import { ShadingComponent, ShadingConfig } from './Shading';

type ComponentBuildEdge = {
    sensorsByType: Partial<Record<FacadeComponentType, SensorData[]>>;
    p1: FVector;
    p2: FVector;
    height: number;
    thickness: number;
    directionByType: Partial<Record<FacadeComponentType, FVector | undefined>>;
    isFacingByType: Partial<Record<FacadeComponentType, boolean>>;
    isAdjacentByType: Partial<Record<FacadeComponentType, boolean>>;
    perimeter: FVector[];
    renderedEdgeIndicesByType: Partial<Record<FacadeComponentType, Set<number>>>;
    visibilityLoops: FVector[][];
};

type ComponentBuildParams = {
    edge: ComponentBuildEdge;
    facadeConfig: FloorBasedFacadeState;
};

type ComponentMaterials = {
    pvPrimary: THREE.MeshBasicMaterial;
    pvSecondary: THREE.MeshBasicMaterial;
    balcony: THREE.MeshBasicMaterial;
    green: THREE.MeshBasicMaterial;
    shading: THREE.MeshBasicMaterial;
};

export class FacadeComponentBuilder {
    static buildComponents(params: ComponentBuildParams): Component[] {
        const selectedTypes = params.facadeConfig.componentTypes;
        if (selectedTypes.length === 0) return [];
        return selectedTypes
            .map((componentType) => FacadeComponentBuilder.buildSingleComponent(params, componentType))
            .filter((component): component is Component => component !== undefined);
    }

    private static buildSingleComponent(params: ComponentBuildParams, componentType: FacadeComponentType): Component | undefined {
        const edge = params.edge;
        const materials = FacadeComponentBuilder.createMaterials();
        const edgeIndex = edge.perimeter.findIndex((point) => point.x === edge.p1.x && point.y === edge.p1.y);
        const requireActiveSensors = params.facadeConfig.componentLink;
        const edgeSensors = edge.sensorsByType[componentType] ?? [];
        const edgeDirection = edge.directionByType[componentType];
        const isFacing = edge.isFacingByType[componentType] ?? false;
        const isAdjacent = edge.isAdjacentByType[componentType] ?? false;
        const renderedEdgeIndices = edge.renderedEdgeIndicesByType[componentType] ?? new Set<number>();

        // If no facades are available for the component (Based on Floor.ts) skip:
        if (!renderedEdgeIndices.has(edgeIndex)) return undefined;

        const baseContext = {
            p1: edge.p1,
            p2: edge.p2,
            thickness: edge.thickness,
            sensors: edgeSensors,
            direction: edgeDirection,
            perimeter: edge.perimeter,
            visibilityLoops: edge.visibilityLoops,
            requireActiveSensors,
            edgeIndex,
            renderedIndices: renderedEdgeIndices,
        };

        if (componentType === FacadeComponentType.PV_PANELS) {
            const coverageMode = params.facadeConfig.verticalCoverageMode;
            const useSecondaryMaterial =
                (coverageMode === VerticalFacadeCoverageMode.ALL && !isFacing) ||
                (coverageMode === VerticalFacadeCoverageMode.FACING_AND_ADJACENT && isAdjacent);
            const renderWhenBlocked =
                coverageMode === VerticalFacadeCoverageMode.ALL ||
                (coverageMode === VerticalFacadeCoverageMode.FACING_AND_ADJACENT && isAdjacent) ||
                isFacing;
            const context: FacadeComponentContext = {
                ...baseContext,
                height: edge.height + edge.thickness,
                material: useSecondaryMaterial ? materials.pvSecondary : materials.pvPrimary,
                renderWhenBlocked,
            };
            const config: PvPanelsConfig = {
                panelHeight: params.facadeConfig.verticalHeight.value,
                panelLength: params.facadeConfig.verticalLength.value,
                panelOffset: params.facadeConfig.pvOffset.value,
                connectCorners: params.facadeConfig.verticalConnectCorners,
                closed: params.facadeConfig.verticalClosed,
                panelAngle: params.facadeConfig.verticalAngle.value,
                useCustomAngle: params.facadeConfig.verticalCustomAngle,
                facadeConfig: params.facadeConfig,
            };

            return new PvPanelsComponent(context, config);
        }

        if (componentType === FacadeComponentType.BALCONIES) {
            const context: FacadeComponentContext = {
                ...baseContext,
                height: edge.height,
                material: materials.balcony,
                renderWhenBlocked: true,
            };

            const config: BalconyConfig = {
                depth: params.facadeConfig.balconyLength.value,
                connectCorners: params.facadeConfig.balconyConnectCorners,
            };
            return new BalconyComponent(context, config);
        }

        if (componentType === FacadeComponentType.GREEN) {
            const context: FacadeComponentContext = {
                ...baseContext,
                height: edge.height + edge.thickness,
                material: materials.green,
                renderWhenBlocked: true,
            };
            const config: GreenConfig = {
                depth: params.facadeConfig.greenDepth.value,
            };
            return new GreenComponent(context, config);
        }

        if (componentType === FacadeComponentType.SHADING) {
            const context: FacadeComponentContext = {
                ...baseContext,
                height: edge.height,
                material: materials.shading,
                renderWhenBlocked: true,
            };
            const config: ShadingConfig = {
                depth: params.facadeConfig.shadingDepth.value,
                connectCorners: params.facadeConfig.shadingConnectCorners,
                manual: params.facadeConfig.shadingManual,
                automaticRectangle: params.facadeConfig.shadingAutomaticRectangle,
                automaticRectangleReference: params.facadeConfig.shadingAutomaticRectangleReference,
                automaticLateralVerticalShade: params.facadeConfig.shadingAutomaticLateralVerticalShade,
            };
            return new ShadingComponent(context, config);
        }

        return undefined;
    }

    private static createMaterials(): ComponentMaterials {
        const materials: ComponentMaterials = {
            pvPrimary: new THREE.MeshBasicMaterial({
                color: 0xffd966,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
            pvSecondary: new THREE.MeshBasicMaterial({
                color: 0xffec99,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
            balcony: new THREE.MeshBasicMaterial({
                color: 0xb0b0b0,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
            green: new THREE.MeshBasicMaterial({
                color: 0x5c9f3e,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
            shading: new THREE.MeshBasicMaterial({
                color: 0x9aa7b3,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
        };

        tagDesignSchemeSurfaceMaterial(materials.pvPrimary, 'pv');
        tagDesignSchemeSurfaceMaterial(materials.pvSecondary, 'pv');
        tagDesignSchemeSurfaceMaterial(materials.balcony, 'opaque');
        tagDesignSchemeSurfaceMaterial(materials.green, 'opaque');
        tagDesignSchemeSurfaceMaterial(materials.shading, 'opaque');
        return materials;
    }
}
